// src/components/Schedule/BuildSchedule/ScorePopover.jsx

import React, { useState, useLayoutEffect, useRef } from "react";

/**
 * Computes a breakdown of positive/negative/neutral points
 * based on the schedule combination, user preferences, etc.
 *
 * @param {Array} combination - The schedule's courses array.
 * @param {Object} preferences - The user’s schedule preferences (e.g., desired start/end).
 * @param {Function} parseTime - Function to convert "HH:MM" to integer minutes.
 * @returns {Array} A list of objects with { label, type: "negative" | "positive" | "neutral" }.
 */
export function getScoreBreakdown(combination, preferences, parseTime) {
  if (!combination || combination.length === 0) {
    return [{ label: "No courses in combination", type: "neutral" }];
  }

  // Separate valid (non-TBA) meetings from TBA ones
  const allMeetings = [];
  let tbaCount = 0;

  combination.forEach((course) => {
    if (course.sfuData && Array.isArray(course.sfuData.courseSchedule)) {
      course.sfuData.courseSchedule.forEach((m) => {
        if (m.isExam) return; // skip exams

        // Check if times are missing or TBA
        const startStr = (m.startTime || "").toLowerCase();
        const endStr = (m.endTime || "").toLowerCase();
        if (!startStr || startStr.includes("tba") || !endStr || endStr.includes("tba")) {
          tbaCount++;
          return;
        }

        // If it's valid, push to allMeetings
        allMeetings.push(m);
      });
    }
  });

  // If absolutely everything was TBA (or no valid times found)
  if (allMeetings.length === 0) {
    if (tbaCount > 0) {
      // All times are TBA
      return [
        {
          label: `${tbaCount} TBA time${tbaCount > 1 ? "s" : ""}`,
          type: "neutral",
        },
        { label: "No specific meeting times to analyze", type: "neutral" },
      ];
    }
    // Otherwise, no TBA but still no valid times
    return [{ label: "No meeting times found", type: "neutral" }];
  }

  // Now we have at least some valid meeting times
  const breakdown = [];
  breakdown.push({
    label: `${combination.length} course${combination.length > 1 ? "s" : ""}`,
    type: "neutral",
  });

  // If there were TBA times as well, mention them (neutrally)
  if (tbaCount > 0) {
    breakdown.push({
      label: `${tbaCount} TBA time${tbaCount > 1 ? "s" : ""}`,
      type: "neutral",
    });
  }

  // Extract basic preference data
  const desiredStart = parseTime(preferences?.desiredStart || "08:00");
  const desiredEnd = parseTime(preferences?.desiredEnd || "17:00");
  const lunchStart = parseTime(preferences?.lunchStart || "12:00");
  const lunchEnd = parseTime(preferences?.lunchEnd || "13:00");

  // Find earliest and latest among valid meetings
  const globalEarliest = Math.min(
    ...allMeetings.map((m) => parseTime(m.startTime || "0:00"))
  );
  const globalLatest = Math.max(
    ...allMeetings.map((m) => parseTime(m.endTime || "0:00"))
  );

  // Negative points if starting too early or ending too late
  if (globalEarliest < desiredStart) {
    breakdown.push({
      label: `Starts ${desiredStart - globalEarliest} min earlier than desired`,
      type: "negative",
    });
  }
  if (globalLatest > desiredEnd) {
    breakdown.push({
      label: `Ends ${globalLatest - desiredEnd} min after desired`,
      type: "negative",
    });
  }

  // Compare total day length to the preferred window
  const desiredDayLength = desiredEnd - desiredStart;
  const actualDayLength = globalLatest - globalEarliest;
  if (actualDayLength > desiredDayLength) {
    breakdown.push({
      label: `Long day (+${actualDayLength - desiredDayLength} min)`,
      type: "negative",
    });
  }

  // Lunch conflicts
  let lunchConflicts = 0;
  allMeetings.forEach((m) => {
    const mStart = parseTime(m.startTime || "0:00");
    const mEnd = parseTime(m.endTime || "0:00");
    if (mStart < lunchEnd && mEnd > lunchStart) {
      lunchConflicts++;
    }
  });
  if (lunchConflicts > 0) {
    breakdown.push({
      label: `${lunchConflicts} lunch conflict${lunchConflicts > 1 ? "s" : ""}`,
      type: "negative",
    });
  }

  // Positive if it starts on/after desired time
  if (globalEarliest >= desiredStart) {
    breakdown.push({ label: "On-time start", type: "positive" });
  }
  // Positive if ends on/before desired time
  if (globalLatest <= desiredEnd) {
    breakdown.push({ label: "On-time finish", type: "positive" });
  }
  // Positive if no lunch conflicts
  if (lunchConflicts === 0) {
    breakdown.push({ label: "No lunch conflicts", type: "positive" });
  }

  // If "after class breaks" are enabled, check for short breaks
  if (preferences?.afterClassBreakEnabled) {
    let hasShortBreak = false;
    const dayMeetings = {};

    allMeetings.forEach((m) => {
      if (!m.days) return;
      const days = m.days.split(",").map((d) => d.trim());
      const start = parseTime(m.startTime || "0:00");
      const end = parseTime(m.endTime || "0:00");

      days.forEach((day) => {
        if (!dayMeetings[day]) dayMeetings[day] = [];
        dayMeetings[day].push({ start, end });
      });
    });

    Object.keys(dayMeetings).forEach((day) => {
      const meetings = dayMeetings[day].sort((a, b) => a.start - b.start);
      for (let i = 0; i < meetings.length - 1; i++) {
        const gap = meetings[i + 1].start - meetings[i].end;
        if (
          gap > 0 &&
          gap < parseInt(preferences.afterClassBreakDuration, 10)
        ) {
          hasShortBreak = true;
        }
      }
    });

    if (!hasShortBreak) {
      breakdown.push({ label: "Adequate break times", type: "positive" });
    }
  }

  // If we only have the initial neutral items at this point, add something
  // so the user sees a positive message
  if (breakdown.length === 1) {
    breakdown.push({ label: "Meets all preferences", type: "positive" });
  }

  return breakdown;
}

/**
 * Named export for the ScorePopover component.
 *
 * Note: We now accept an extra prop, `anchorRef`, which should be a ref
 * to the button (or element) that the popover is attached to.
 */
export function ScorePopover({ combination, preferences, parseTime, visible, anchorRef }) {
  const popoverRef = useRef(null);
  const [placement, setPlacement] = useState("top");

  // When the popover becomes visible, check if there's enough space above the anchor.
  useLayoutEffect(() => {
    if (visible && anchorRef?.current && popoverRef.current) {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();

      // If there's not enough space above (add an 8px margin), switch to bottom.
      if (anchorRect.top < popoverRect.height + 8) {
        setPlacement("bottom");
      } else {
        setPlacement("top");
      }
    }
  }, [visible, anchorRef, combination]);

  // Dynamically choose positioning classes based on the placement state.
  const popoverPositionClasses =
    placement === "top" ? "bottom-full left-0 mb-2" : "top-full left-0 mt-2";

  // Generate the breakdown from the combination.
  const breakdown = getScoreBreakdown(combination, preferences, parseTime);

  return (
    <div
      ref={popoverRef}
      data-popover
      id="popover-no-arrow"
      role="tooltip"
      className={`absolute z-10 w-80 bg-white border border-gray-200 rounded-lg transition-opacity duration-300 ${
        visible ? "opacity-100 visible" : "opacity-0 invisible"
      } ${popoverPositionClasses}`}
    >
      <div className="px-3 py-2 bg-gray-100 border-b border-gray-200 rounded-t-lg dark:border-gray-600 dark:bg-gray-700">
        <h3 className="font-semibold text-gray-900 dark:text-white">
          Schedule Score Breakdown
        </h3>
      </div>
      <div className="px-3 py-2">
        <p className="mb-2 text-sm text-gray-600">
          Your schedule’s score is based on start/end times, lunch break adherence, gap lengths
          between classes, and more. (TBA times are not penalized.)
        </p>
        <div className="flex flex-wrap gap-2">
          {breakdown.map((item, idx) => (
            <span
              key={idx}
              className={`text-xs font-medium px-2.5 py-0.5 rounded-sm ${
                item.type === "negative"
                  ? "bg-red-100 text-red-800 dark:text-red-400 border border-red-400"
                  : item.type === "positive"
                  ? "bg-green-100 text-green-800 dark:text-green-400 border border-green-400"
                  : "bg-blue-100 text-blue-800 dark:text-blue-400 border border-blue-400"
              }`}
            >
              {item.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
